/* Copyright (c) 2021-2026, NVIDIA CORPORATION.  All rights reserved.
*
* NVIDIA CORPORATION and its licensors retain all intellectual property
* and proprietary rights in and to this software, related documentation
* and any modifications thereto.  Any use, reproduction, disclosure or
* distribution of this software and related documentation without an express
* license agreement from NVIDIA CORPORATION is strictly prohibited.
*/

/** This file was automatically generated. DO NOT MODIFY IT BY HAND. */

/* eslint-disable @typescript-eslint/naming-convention */

export module NvworkstationTypes {

/** This object was automatically generated by json-schema-to-typescript. DO NOT MODIFY IT BY HAND. */

export interface NvWorkstation {
    [k: string]: any;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "RtxProSyncDisplayInfo".
 */
export interface RtxProSyncDisplayInfo {
    /**
     * The NvCplApi display ID corresponding to this display.
     */
    nvDisplayId?: number;
    /**
     * Can this display be a master?
     */
    bCanBeServer?: boolean;
    ulSyncState?: SyncState;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "RtxProSyncGpuInfo".
 */
export interface RtxProSyncGpuInfo {
    /**
     * Gpu ID (also referreed to as the NvApi Gpu handle)
     */
    ulGpuId?: number;
    ulConnectorLocation?: RtxProSyncGpuConnector;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "RtxProSyncBasicInfo".
 */
export interface RtxProSyncBasicInfo {
    /**
     * strFirmwareVersion
     */
    strFirmwareVersion?: string;
    /**
     * ulBoardId
     */
    ulBoardId?: number;
    /**
     * Indicates if multiplication/division of the frequency of house sync signal is supported.
     */
    bIsMulDivSupported?: boolean;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "RtxProSyncControlInfo".
 */
export interface RtxProSyncControlInfo {
    /**
     * RTX-Pro-Sync board index
     */
    ulRtxProSyncIndex?: number;
    ulSyncSource?: SyncSource;
    /**
     * Indicates if house sync is an output (valid only when syncSource is NVAPI_GSYNC_SYNC_SOURCE_VSYNC on P2061 boards)
     */
    bSyncSourceIsOutput?: boolean;
    ulSyncEdge?: SyncEdge;
    ulMulDivMode?: MulDivMode;
    /**
     * Indicates the multiplier/divider value for the housesync signal.
     */
    ulMultiplyDivideValue?: number;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "RtxProSyncStatusInfo".
 */
export interface RtxProSyncStatusInfo {
    /**
     * Indicates if an incoming frame lock sync pulse is present on an RJ45 port
     */
    bFramelockSyncPulse?: boolean;
    /**
     * Current IO mode (framelock) of this RJ45 port
     */
    ulRJ45_Framelock_Ports?: Rj45FramelockPort[];
    /**
     * Indicates if an external BNC (house) sync signal is present
     */
    bExternalSyncSignalPresent?: boolean;
    /**
     * Incoming house sync frequency in Hz
     */
    strExternalSyncSignalFreq?: string;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "RtxProSyncTopologyInfo".
 */
export interface RtxProSyncTopologyInfo {
    /**
     * RTX-Pro-Sync board index
     */
    ulRtxProSyncIndex?: number;
    gpuInfos?: RtxProSyncGpuInfo[];
    displayInfos?: RtxProSyncDisplayInfo[];
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "OutputInfo".
 */
export interface OutputInfo {
    /**
     * The NvCplApi output ID associated with this gpu-connector output
     */
    ulNvOutputID?: number;
    /**
     * The NvCplApi display ID associated with this gpu-connector output
     */
    ulNvDisplayID?: number;
    /**
     * True if this gpu-connector has a connected display (either physical or faked via forced EDID)
     */
    bConnectedDisplay?: boolean;
    /**
     * True if this gpu-connector has an active display
     */
    bActiveDisplay?: boolean;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GpuConnectorInfo".
 */
export interface GpuConnectorInfo {
    ulType?: GpuConnectorType;
    /**
     * The location index of this gpu-connector (corresponding to physical connector location?)
     */
    ulLocationIndex?: number;
    /**
     * Array of outputs corresponding to the connector
     */
    outputInfos?: OutputInfo[];
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "PciAddress".
 */
export interface PciAddress {
    /**
     * The PCI Domain
     */
    ulDomain: number;
    /**
     * The PCI Bus
     */
    ulBus: number;
    /**
     * The PCI Device
     */
    ulDevice: number;
    /**
     * The PCI Function
     */
    ulFunction: number;
}
/**
 * Advanced timing details for this display
 *
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "DisplayTiming".
 */
export interface DisplayTiming {
    HVisible?: number;
    HBorder?: number;
    HFrontPorch?: number;
    HSyncWidth?: number;
    HBackPorch?: number;
    HSyncPol?: number;
    VVisible?: number;
    VBorder?: number;
    VFrontPorch?: number;
    VSyncWidth?: number;
    VBackPorch?: number;
    VSyncPol?: number;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetRtxProSyncCountReturn".
 */
export interface GetRtxProSyncCountReturn {
    ulStatus: Status;
    /**
     * RTX-Pro-Sync board count
     */
    ulRtxProSyncCount: number;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetRtxProSyncBasicInfoParameters".
 */
export interface GetRtxProSyncBasicInfoParameters {
    /**
     * RTX-Pro-Sync board index
     */
    ulRtxProSyncIndex: number;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetRtxProSyncBasicInfoReturn".
 */
export interface GetRtxProSyncBasicInfoReturn {
    ulStatus: Status;
    rtxProSyncBasicInfo: RtxProSyncBasicInfo;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetRtxProSyncControlInfoParameters".
 */
export interface GetRtxProSyncControlInfoParameters {
    /**
     * RTX-Pro-Sync board index
     */
    ulRtxProSyncIndex: number;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetRtxProSyncControlInfoReturn".
 */
export interface GetRtxProSyncControlInfoReturn {
    ulStatus: Status;
    rtxProSyncControlInfo: RtxProSyncControlInfo;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetRtxProSyncStatusInfoParameters".
 */
export interface GetRtxProSyncStatusInfoParameters {
    /**
     * RTX-Pro-Sync board index
     */
    ulRtxProSyncIndex: number;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetRtxProSyncStatusInfoReturn".
 */
export interface GetRtxProSyncStatusInfoReturn {
    ulStatus: Status;
    rtxProSyncStatusInfo: RtxProSyncStatusInfo;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetRtxProSyncTopologyInfoParameters".
 */
export interface GetRtxProSyncTopologyInfoParameters {
    /**
     * RTX-Pro-Sync board index
     */
    ulRtxProSyncIndex: number;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetRtxProSyncTopologyInfoReturn".
 */
export interface GetRtxProSyncTopologyInfoReturn {
    ulStatus: Status;
    rtxProSyncTopologyInfo: RtxProSyncTopologyInfo;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetGpuConnectorInfoParameters".
 */
export interface GetGpuConnectorInfoParameters {
    /**
     * Gpu index for which information is required.
     */
    ulGpuIndex: number;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetGpuConnectorInfoReturn".
 */
export interface GetGpuConnectorInfoReturn {
    ulStatus: Status;
    gpuConnectorInfos: GpuConnectorInfo[];
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetGpuPciAddressParameters".
 */
export interface GetGpuPciAddressParameters {
    /**
     * Gpu index for which information is required.
     */
    ulGpuIndex: number;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetGpuPciAddressReturn".
 */
export interface GetGpuPciAddressReturn {
    ulStatus: Status;
    pciAddress: PciAddress;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetDisplayTimingParameters".
 */
export interface GetDisplayTimingParameters {
    /**
     * Target gpu index
     */
    ulGpuIndex: number;
    /**
     * Target gisplay index
     */
    ulDisplayIndex: number;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetDisplayTimingReturn".
 */
export interface GetDisplayTimingReturn {
    ulStatus: Status;
    displayTiming: DisplayTiming;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "WriteJsonDataToFileParameters".
 */
export interface WriteJsonDataToFileParameters {
    /**
     * Topology data (in JSON format) to be written to file
     */
    strFileData: string;
    /**
     * File path + name to store the topology data
     */
    strFilePath: string;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "WriteJsonDataToFileReturn".
 */
export interface WriteJsonDataToFileReturn {
    ulStatus: Status;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetPciConnectivityTypeParameters".
 */
export interface GetPciConnectivityTypeParameters {
    pciAddress1: PciAddress;
    pciAddress2: PciAddress;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GetPciConnectivityTypeReturn".
 */
export interface GetPciConnectivityTypeReturn {
    ulStatus: Status;
    ulPciConnectivityType: PciConnectivityType;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "IsForcedEdidParameters".
 */
export interface IsForcedEdidParameters {
    /**
     * Target Gpu index
     */
    ulGpuIndex: number;
    /**
     * The NvCplApi output ID associated with this gpu-connector output
     */
    ulNvOutputID: number;
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "IsForcedEdidReturn".
 */
export interface IsForcedEdidReturn {
    ulStatus: Status;
    /**
     * Indicates whether the given ulGpuIndex + ulNvOutputID has a forced EDID. Invalid output IDs intentionally return a successful false result.
     */
    bForcedEdid: boolean;
}

/**
 * Return status of NvWorkstation plugin functions. 'SUCCESS' indicates valid return data
 *
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "Status".
 */
export enum Status {
    SUCCESS = 0,
    GENERIC_ERROR = 1,
    INVALID_ARGUMENT = 2,
    DEVICE_NOT_FOUND = 3,
    API_FUNC_NOT_FOUND = 4,
    API_FUNC_ERROR = 5,
    NO_GPU_SLI_GROUPS = 6,
    INVALID_USER_PRIVILEGE = 7,
    DOCUMENT_PARSE_ERROR = 8,
    DOCUMENT_WRITE_ERROR = 9
}
/**
 * VSync/House sync
 *
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "SyncSource".
 */
export enum SyncSource {
    VSync = 0,
    HouseSync = 1
}
/**
 * Indicates leading edge, falling edge, or both
 *
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "SyncEdge".
 */
export enum SyncEdge {
    Rising = 0,
    Falling = 1,
    Both = 2
}
/**
 * Indicates multiplier/divider mode for the housesync signal. (syncSource needs to be set as NVAPI_GSYNC_SYNC_SOURCE_HOUSESYNC)
 *
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "MulDivMode".
 */
export enum MulDivMode {
    Undefined = 0,
    Multiply = 1,
    Divide = 2
}
/**
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "RJ45_Framelock_Port".
 */
export enum Rj45FramelockPort {
    Output = 0,
    Input = 1,
    Unused = 2
}
/**
 * Indicates which connector the GPU is connected to on the RTX Pro Sync device
 *
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "RtxProSyncGpuConnector".
 */
export enum RtxProSyncGpuConnector {
    None = 0,
    GPU0 = 1,
    GPU1 = 2,
    GPU2 = 3,
    GPU3 = 4
}
/**
 * Display sync state of: unsync'd, slave, or master
 *
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "SyncState".
 */
export enum SyncState {
    Unsynced = 0,
    Slave = 1,
    Master = 2
}
/**
 * The type of this gpu-connector
 *
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "GpuConnectorType".
 */
export enum GpuConnectorType {
    VGA = 0,
    DVI = 1,
    COMPOSITE = 2,
    SVIDEO = 3,
    COMPONENT = 4,
    HDMI = 5,
    DISPLAYPORT = 6,
    USBC = 7,
    UNKNOWN = 8
}
/**
 * Indicates the connectivity type between 2 PCI addresses
 *
 * This interface was referenced by `NvWorkstation`'s JSON-Schema
 * via the `definition` "PciConnectivityType".
 */
export enum PciConnectivityType {
    SYS = 0,
    NODE = 1,
    PHB = 2,
    PXB = 3,
    PIX = 4
}


}
